import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Item, ItemImage } from "../models/item_model";
import { getItemList } from "../services/api_service";
import WappIcon from "../assets/icons/wapp_white.png";
import { CardList } from "./components/card_list";
import { Footer } from "./components/footer";
import { useCart } from "../context/cart_context";
import { toast } from "react-toastify";
import { useMediaQuery } from "react-responsive";
import ImageModal from "./components/zoom_modal";

export default function ProductDetailPage() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const itemId = queryParams.get("id");

  const { cartItems, addToCart, clearCart } = useCart();
  const [product, setProduct] = useState<Item | null>(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [showDescription, setShowDescription] = useState(true);
  const [celebItems, setCelebItems] = useState<Item[]>([]);
  const navigate = useNavigate();
  const [previewImages, setPreviewImages] = useState<ItemImage[] | null>(null);
  const [buyNowLoader, setBuyNowLoader] = useState(false);

  const isMobile = useMediaQuery({ maxWidth: 768 });

  // find if product is already in cart
  let cartItem = cartItems.find((i) => i.item_id === Number(itemId));
  let maxStockReached =
    product && cartItem && (cartItem.added_quantity ?? 0) >= product.quantity;
  const [currentIndex, setCurrentIndex] = useState(0);
  const userId = localStorage.getItem("userId") ?? undefined;

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollLeft = e.currentTarget.scrollLeft;
    const width = window.innerWidth;
    const newIndex = Math.round(scrollLeft / width);
    setCurrentIndex(newIndex);
  };

  useEffect(() => {
    const fetchProduct = async () => {
      if (itemId) {
        setLoading(true);
        const data = await getItemList({ itemId: itemId, userId: userId });
        if (data && data.records.length > 0) {
          const fetchedProduct = data.records[0];
          setProduct(fetchedProduct);
        }

        const celebItemsResp = await getItemList({
          isCelebrityChoice: true,
          pageSize: 10,
        });
        setCelebItems(celebItemsResp?.records || []);

        setLoading(false);
      }
    };
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchProduct();
  }, [itemId]);

  const handleQuantityChange = (delta: number) => {
    setQuantity((prev) => {
      const newQty = prev + delta;

      if (newQty < 1) {
        toast.info("You cannot select less than 1 item");
        return 1;
      }

      if (product && newQty > product.quantity) {
        toast.info(`Only ${product.quantity} item(s) available`);
        return product.quantity;
      }

      return newQty;
    });
  };

  const handleAddToCart = () => {
    if (!localStorage.getItem("userId")) {
      navigate("/login");
      return;
    }
    if (!product) return;

    const existingCartItem = cartItems.find((ci: any) => ci.item_id === product.item_id);
    const alreadyAddedQty = existingCartItem?.added_quantity || 0;
    const remainingQty = product.quantity - alreadyAddedQty;

    if (remainingQty <= 0) {
      toast.warning("You have already added the maximum quantity for this item!");
      return;
    }

    const quantityToAdd = Math.min(quantity, remainingQty);
    if (quantityToAdd <= 0) {
      toast.info(`Only ${remainingQty} item(s) can be added`);
      return;
    }
    addToCart(product, quantityToAdd);
    toast.success(`${quantityToAdd} item(s) added to cart ✅`);
  };

  const handleBuyNow = async () => {
    if (!localStorage.getItem("userId")) {
      navigate("/login");
      return;
    }
    if (!product) return;

    if (product.quantity <= 0) {
      toast.warning("This product is out of stock");
      return;
    }

    if (quantity > product.quantity) {
      toast.warning("Selected quantity not available");
      return;
    }
    setBuyNowLoader(true);
    await clearCart();
    await addToCart(product, quantity, true);
    setBuyNowLoader(false);
    navigate("/cart");
  };

  // ------------------------ RENDER METHODS ------------------------
  const renderWebView = () => {
    return (
      <div style={styles.page}>
        <div style={styles.headerContainer}>
          <div style={styles.header}>
            <span style={styles.headerText}>
              Home / Collections / {product?.title || "Loading..."}
            </span>
          </div>
        </div>

        {buyNowLoader && (
          <div style={styles.overlay}>
            <div style={styles.spinner}></div>
          </div>
        )}


        <div style={styles.mainContainer}>
          {loading ? (
            <div style={styles.loader}>
              <div className="spinner"></div>
              <p>Loading product...</p>
            </div>
          ) : product ? (
            <div style={styles.productLayout}>
              <div style={{ ...styles.imageSection, overflowY: "auto", }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "10px",
                    flex: 1
                  }}
                >
                  {product.item_images.map((img, index) => (
                    <div
                      key={index}
                      onClick={() => setPreviewImages(product.item_images)} style={{
                        border:
                          "1px solid #ddd",
                        borderRadius: 8,
                        cursor: "pointer",
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={img.document_url}
                        alt={`img-${index}`}
                        loading="lazy"
                        style={{
                          width: "100%",
                           height: "100%",
                          objectFit: "cover",
                          display: "block",
                          background: "#fff",
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>


              <div style={styles.detailsWrapper}>
                <h2 style={styles.productTitle}>{product.title}</h2>
                <hr style={styles.divider} />

                <div style={styles.row}>
                  <div style={styles.styleNoContainer}>
                    <span style={styles.styleNoLabel}>STYLE NO</span>
                    <span style={styles.styleNoValue}>{product.item_number}</span>
                  </div>
                  <span
                    style={{
                      ...styles.stock,
                      backgroundColor: product.quantity > 0 ? "#e8f5e9" : "#ffebee",
                      color: product.quantity > 0 ? "#2e7d32" : "#c62828",
                    }}
                  >
                    {product.quantity > 0 ? "In Stock" : "Out of Stock"}
                  </span>
                </div>

                <div style={styles.priceRow}>
                  <span style={styles.finalPrice}>₹{product?.final_price  || "N/A"}</span>
                  {product.discount_percentage > 0 && (
                    <>
                      <span style={styles.originalPrice}>₹{product.original_price}</span>
                      <span style={styles.discount}>{product.discount_percentage}% OFF</span>
                    </>
                  )}
                </div>

                {product.quantity <= 0 ? (
                  <div
                    style={{
                      fontWeight: "700",
                      fontSize: "18px",
                      color: "#e91e63",
                      marginTop: "16px",
                    }}
                  >
                    🚫 Product Not Available
                  </div>
                ) : (
                  <>
                    <div style={styles.cartRow}>
                      {!maxStockReached ? (
                        <>
                          <div style={styles.quantityBox}>
                            <button
                              style={styles.qtyButton}
                              onClick={() => handleQuantityChange(-1)}
                            >
                              -
                            </button>
                            <span style={styles.qtyValue}>{quantity}</span>
                            <button
                              style={styles.qtyButton}
                              onClick={() => handleQuantityChange(1)}
                            >
                              +
                            </button>
                          </div>

                          <button style={styles.cartButton} onClick={handleAddToCart}>
                            Add to Cart
                          </button>
                        </>
                      ) : (
                        <div style={styles.maxQtyReached}>
                          <span style={styles.maxQtyText}>Maximum Quantity Added</span>
                        </div>
                      )}
                    </div>

                    {!maxStockReached && (
                      <button onClick={handleBuyNow} style={styles.buyNowButton}>Buy Now</button>
                    )}

                    {/* {!maxStockReached && (
                      <button style={styles.whatsappButton}>
                        <img
                          src={WappIcon}
                          alt="WhatsApp"
                          style={{ width: 20, height: 20, marginRight: 6 }}
                        />
                        Order on WhatsApp
                      </button>
                    )} */}
                  </>
                )}

                <ul style={styles.highlights}>
                  <li>🚚 Free Delivery on all orders within India</li>
                  <li>↩️ Easy 15 days returns & exchange</li>
                  <li>📦 Free Shipping</li>
                  <li>✅ Assured Quality</li>
                </ul>

                <div style={{
                  backgroundColor: "#F8F1F1",
                  borderRadius: 12,
                }}>
                  <div
                    onClick={() => setShowDescription(!showDescription)}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                      marginBottom: showDescription ? "16px" : "0",
                    }}
                  >
                    <span style={{ fontSize: "19px", fontWeight: "normal" }}>Product Details</span>
                    <button style={{
                      background: "none",
                      border: "none",
                      fontSize: "24px",
                      cursor: "pointer"
                    }}>{showDescription ? "−" : "+"}</button>
                  </div>

                  {showDescription && (
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "12px",
                    }}>
                      {product.item_details?.map((detail: any, idx: number) => (
                        <div key={idx} style={{
                          display: "flex",
                          flexDirection: "column",
                          padding: "8px",

                          borderRadius: "8px"
                        }}>
                          <span style={{ fontSize: "16px", color: "#333" }}>{detail.detail_name}</span>
                          <span style={{ fontSize: "17px", fontWeight: "700", color: "#111", marginTop: "4px" }}>
                            {detail.detail_value}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              </div>
            </div>
          ) : (
            <p>Product not found</p>
          )}
        </div>
        {previewImages && (
          <ImageModal
            images={previewImages}
            onClose={() => setPreviewImages(null)}
          />
        )}

        {celebItems.length > 0 && (
          <div style={{ marginTop: "40px", width: "100%" }}>
            <CardList
              title="Similar Products"
              items={celebItems}
              onShowMore={() => {
                navigate("/result", {
                  state: { isCelebrityChoice: true, headerTitle: "Similar Products" },
                });
              }}
            />
          </div>
        )}

        <div style={{ marginTop: "40px", width: "100%" }}>
          <Footer />
        </div>
      </div>
    );
  };

  
  const renderMobileView = () => (
    <div >
      {loading ? (
        <div style={{...styles.loader, marginTop: "10%"}}>
          <div className="spinner"></div>
          <p>Loading product...</p>
        </div>
      ) : product ? (
        <>
          {buyNowLoader && (
          <div style={styles.overlay}>
            <div style={styles.spinner}></div>
          </div>
        )}

         {previewImages && (
          <ImageModal
            images={previewImages}
            onClose={() => setPreviewImages(null)}
          />
        )}
          {/* Horizontal image list */}
          <div
            style={{
              position: "relative",
              width: "100%",
              overflow: "hidden",
            }}
          >
            {/* Image Strip */}
            <div
              style={{
                display: "flex",
                overflowX: "scroll",
                scrollSnapType: "x mandatory",
                scrollbarWidth: "none", // Firefox
                msOverflowStyle: "none", // IE
              }}
              onScroll={handleScroll}
            >
              {product.item_images.map((img, idx) => (
                <div
                  key={idx}
                    onClick={() => setPreviewImages(product.item_images)}
                  style={{
                    flex: "0 0 100%", // each image takes 100% width
                    height: "65vh",
                    scrollSnapAlign: "start",
                    padding: "0 4px", // small padding so border radius is visible
                    boxSizing: "border-box",
                  }}
                >
                  <img
                    src={img.document_url}
                    alt={`img-${idx}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "12px",
                      display: "block",
                    }}
                  />
                </div>
              ))}
            </div>

            {/* Pagination Dots */}
            <div
              style={{
                position: "absolute",
                bottom: 10,
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                gap: 6,
                backgroundColor: "rgba(0,0,0,0.4)",
                padding: "6px 12px",
                borderRadius: 20,
              }}
            >
              {product.item_images.map((_, idx) => (
                <div
                  key={idx}
                  style={{
                    width: currentIndex === idx ? 10 : 8,
                    height: currentIndex === idx ? 10 : 8,
                    borderRadius: "50%",
                    backgroundColor: currentIndex === idx ? "#fff" : "#aaa",
                    transition: "all 0.3s ease",
                  }}
                />
              ))}
            </div>
          </div>


          {/* Product Name */}
          <div style={{ padding: "16px" }}>
            <h2 style={{ ...styles.productTitle, fontSize: 22 }}>{product.title}</h2>
            <hr style={styles.divider} />

            {/* Style No + Stock */}
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
              <div style={styles.styleNoContainer}>
                <span style={styles.styleNoLabel}>STYLE NO</span>
                <span style={styles.styleNoValue}>{product.item_number}</span>
              </div>
              <span
                style={{
                  ...styles.stock,
                  backgroundColor: product.quantity > 0 ? "#e8f5e9" : "#ffebee",
                  color: product.quantity > 0 ? "#2e7d32" : "#c62828",
                }}
              >
                {product.quantity > 0 ? "In Stock" : "Out of Stock"}
              </span>
            </div>

            {/* Price */}
            <div style={styles.priceRow}>
              <span style={styles.finalPrice}>₹{product?.final_price || 'N/A'}</span>
              {product.discount_percentage > 0 && (
                <>
                  <span style={styles.originalPrice}>₹{product.original_price}</span>
                  <span style={styles.discount}>{product.discount_percentage}% OFF</span>
                </>
              )}
            </div>

            {/* Quantity + Add to Cart in row */}
            <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "12px" }}>
              <div style={styles.quantityBox}>
                <button style={styles.qtyButton} onClick={() => handleQuantityChange(-1)}>-</button>
                <span style={styles.qtyValue}>{quantity}</span>
                <button style={styles.qtyButton} onClick={() => handleQuantityChange(1)}>+</button>
              </div>
              {!maxStockReached ? (
                <button style={{ ...styles.cartButton, flex: 1 }} onClick={handleAddToCart}>Add to Cart</button>
              ) : (
                <div style={styles.maxQtyReached}>
                  <span style={styles.maxQtyText}>Maximum Quantity Added</span>
                </div>
              )}          </div>

            {/* Buy Now Full Width */}
            <button onClick={handleBuyNow} style={styles.buyNowButton}>Buy Now</button>

            {/* WhatsApp Order */}
            {/* <button style={styles.whatsappButton}>
            <img src={WappIcon} alt="WhatsApp" style={{ width: 20, height: 20, marginRight: 6 }} />
            Order on WhatsApp
          </button> */}

            {/* Highlights */}
            <ul style={{ ...styles.highlights, marginTop: 20 }}>
              <li>🚚 Free Delivery on all orders within India</li>
              <li>↩️ Easy 15 days returns & exchange</li>
              <li>📦 Free Shipping</li>
              <li>✅ Assured Quality</li>
            </ul>

            {/* Collapsible Description */}
            <div style={{
              borderRadius: 8,
              marginTop: 16,
              padding: 0
            }}>
              {/* Header */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                  padding: "8px 0"
                }}
                onClick={() => setShowDescription(!showDescription)}
              >
                <span style={{ fontSize: 16, fontWeight: 600 }}>Product Details</span>
                <button style={{
                  background: "none",
                  border: "none",
                  fontSize: 20,
                  cursor: "pointer"
                }}>{showDescription ? "−" : "+"}</button>
              </div>

              {/* Expanded List */}
              {showDescription && (
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px", // spacing between items
                  marginTop: 8,
                  borderRadius: 8,
                }}>
                  {product.item_details?.map((detail, idx) => (
                    <div key={idx} style={{
                      display: "flex",
                      flexDirection: "column",
                    }}>
                      <span style={{ fontSize: 16, color: "#333" }}>{detail.detail_name}</span>
                      <span style={{ fontSize: 17, fontWeight: 700, color: "#111", marginTop: 5 }}>
                        {detail.detail_value}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
          {/* Similar Products */}
          {celebItems.length > 0 && (
            <div style={{ marginTop: "40px" }}>
              <CardList
                title="Similar Products"
                items={celebItems}
                isMobileView={true}
                onShowMore={() =>
                  navigate("/result", {
                    state: { isCelebrityChoice: true, headerTitle: "Similar Products" },
                  })
                }
              />
            </div>
          )}
        </>
      ) : (
        <p>Product not found</p>
      )}
      <div style={{ marginTop: "40px", width: "100%" }}>
        <Footer />
      </div>
    </div>
  );



  return (
    <div style={styles.page}>
      {isMobile ? renderMobileView() : renderWebView()}
    </div>
  );
}



const styles: { [key: string]: React.CSSProperties } = {
  headerContainer: { backgroundColor: "#fff", overflow: "hidden" },
  header: { backgroundColor: "#693B2F", padding: "19px 0" },
  headerText: { color: "#fff", fontSize: 16, fontWeight: 500, margin: "0 5%" },
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.2)", // semi-transparent
    backdropFilter: "blur(4px)", // blur effect
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },
  spinner: {
    width: "50px",
    height: "50px",
    border: "6px solid #f3f3f3", // light gray
    borderTop: "6px solid #e91e63", // pink
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  page: {
    background: "#fafafa",
    fontFamily: "Arimo, sans-serif",
  },
  mainContainer: {
    borderRadius: "20px",
    backgroundColor: "#F8F1F1",
    // background: "linear-gradient(145deg, #ffe6f0, #fff0f6)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    padding: "40px",
    margin: "2% 5%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  loader: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    color: "#555",
  },
  productLayout: {
    display: "flex",
    width: "100%",
    gap: "30px",
  },
  imageSection: {
    flex: "0 0 50%",
    display: "flex",
    gap: "16px",
  },
  thumbnailColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    overflowY: "auto",
    maxHeight: "500px",
  },
  thumbnailImage: {
    width: 60,
    height: 60,
    borderRadius: "8px",
    objectFit: "cover",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  imageWrapper: {
    flex: 1,
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: "0 6px 12px rgba(0,0,0,0.1)",
    background: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  productImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  detailsWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: "20px",
  },
  productTitle: {
    fontSize: "26px",
    fontWeight: "700",
    color: "#222",
  },
  divider: {
    margin: "15px 0",
    borderColor: "#eee",
  },
  infoText: {
    fontSize: "15px",
    color: "#555",
  },
  priceRow: {
    display: "flex",
    alignItems: "baseline",
    gap: "12px",
    marginBottom: "20px",
    marginTop: "10px",

  },
  finalPrice: {
    fontSize: "24px",
    fontWeight: "600",
  },
  originalPrice: {
    textDecoration: "line-through",
    color: "#888",
    fontSize: "14px",
  },
  discount: {
    color: "green",
    fontWeight: "600",
    fontSize: "14px",
  },
  cartRow: {
    display: "flex",
    gap: "12px",
    marginBottom: "12px",
    alignItems: "center",
  },
  cartButton: {
    flex: 1,
    padding: "12px",
    border: "2px solid #e91e63",
    background: "transparent",
    color: "#e91e63",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: "600",
    transition: "all 0.2s ease",
    fontSize: "16px"
  },
  buyNowButton: {
    width: "100%",
    padding: "14px",
    marginTop: "10px",
    background: "#e91e63",
    color: "white",
    fontWeight: "700",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    boxShadow: "0 4px 10px rgba(233,30,99,0.3)",
    transition: "all 0.2s ease",
    fontSize: "16px"
  },
  whatsappButton: {
    width: "100%",
    padding: "14px",
    marginTop: "12px",
    background: "#026734",
    color: "white",
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "600",
    transition: "all 0.2s ease",
    fontSize: "16px"
  },
  highlights: {
    marginTop: "20px",
    listStyle: "none",
    padding: 0,
    color: "#444",
    lineHeight: "1.6",
    fontSize: "18px",
  },

  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: "12px 0",
    gap: "12px",
  },
  styleNoContainer: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #eee",
    borderRadius: "8px",
    backgroundColor: "#fff",
    padding: "2px 2px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  styleNoLabel: {
    backgroundColor: "#F8F1F1", // light pink
    fontSize: "14px",
    padding: "4px 8px",
    borderRadius: "6px",
    marginRight: "8px",
    fontFamily: "Arimo, sans-serif",
  },
  styleNoValue: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#333",
    fontFamily: "Arimo, sans-serif",
    paddingRight: "10px"
  },
  stock: {
    fontWeight: 600,
    padding: "6px 12px",
    borderRadius: "6px",
    fontSize: "14px",
    fontFamily: "Arimo, sans-serif",
  },

  quantityBox: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#fff", // white background
  },
  qtyButton: {
    padding: "12px",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    fontWeight: "700",
    fontSize: "16px",
    color: "#e91e63", // primary color
  },
  qtyValue: {
    padding: "0 14px",
    fontWeight: "600",
    fontSize: "15px",
  },
  descriptionContainer: {
    marginTop: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },

  descriptionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 12px",
    borderRadius: "8px",
    backgroundColor: "#fff",
    border: "1px solid #eee",
    cursor: "pointer",
  },

  descriptionTitle: {
    fontWeight: 600,
    fontSize: "16px",
  },

  expandButton: {
    background: "transparent",
    border: "none",
    fontSize: "18px",
    fontWeight: "600",
    color: "#e91e63",

  },

  descriptionList: {
    marginTop: "8px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    padding: "10px",
    background: "#f8f8f8",
    borderRadius: "8px",
  },

  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "6px 0",
  },

  detailName: { fontWeight: 500, color: "#333" },
  detailValue: { color: "#555", fontSize: 18 },
  maxQtyReached: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    backgroundColor: "#ffe6e6",
    padding: "12px 16px",
    borderRadius: "10px",
    border: "1px solid #e91e63",
    gap: "4px",
  },

  maxQtyText: {
    fontWeight: 700,
    color: "#e91e63",
    fontSize: "16px",
  },

  maxQtySubText: {
    fontSize: "14px",
    color: "#c62828",
  },

};
